import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setRowData } from '../store/slices/counsellingSlice';

/**
 * Migrated from the Angular NavbarComponent bottom-navbar
 * (src/app/views/layout/navbar/navbar.component.{ts,html}).
 *
 * All role-based menu definitions and the `usertypeid` / `emisUsertype1`
 * branching logic are ported verbatim.
 */

// ── Menu model (Angular: menu.model.ts) ──────────────────────────────────────

interface SubMenuItem {
  label: string;
  link: string;
  isTitle?: boolean;
  badge?: { variant: string; text: string };
  visible?: boolean;
}

interface SubMenu {
  subMenuItems: SubMenuItem[];
}

interface MenuItem {
  label: string;
  link?: string;
  icon?: string;
  isMegaMenu?: boolean;
  subMenus?: SubMenu[];
}

// ── Menu definitions per role ────────────────────────────────────────────────

const VIEWMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          { label: 'Candidate List', link: '/counselling/candidate' },
          { label: 'Vacancy', link: '/counselling/vacancy' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          {
            label: 'Transfers in a school',
            link: '/counselling/track-changes',
          },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Summary', link: '/counselling/status' },
          {
            label: 'Candidates Details',
            link: '/counselling/candidates-details',
          },
        ],
      },
    ],
  },
];

const ADMINMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Plan New Counselling',
            link: '/counselling/plan-new-counselling',
          },
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          { label: 'Counselling', link: '/counselling/front-page' },
          { label: 'Seniority Update', link: '/counselling/seniority' },
          {
            label: 'Teacher Transfer',
            link: '/counselling/staff-Transferfilter',
          },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Resultant Vacancy',
            link: '/counselling/resultant-vacancy-entry',
          },
          { label: 'Live Updates', link: '/counselling/live-updates' },
          {
            label: 'Change School Information',
            link: '/counselling/school-info',
          },
          {
            label: 'Change Candidate Details',
            link: '/counselling/change-candidate',
          },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
          {
            label: 'Transfers in a school',
            link: '/counselling/track-changes',
          },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Summary', link: '/counselling/status' },
          {
            label: 'Candidates Details',
            link: '/counselling/candidates-details',
          },
          {
            label: 'Teacher Transfer Report',
            link: '/counselling/TransferReport',
          },
          {
            label: 'Counselling Mapping for Teacher',
            link: '/counselling/mapping',
          },
          { label: 'Candidate Remove', link: '/counselling/candidate-remove' },
        ],
      },
    ],
  },
];

const DEEADMIN: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Counselling', link: '/counselling/front-page' },
          { label: 'Seniority Update', link: '/counselling/seniority' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
          { label: 'Change Candidate', link: '/counselling/change-candidate' },
          {
            label: 'Change School Information',
            link: '/counselling/school-info',
          },
          {
            label: 'School Wise Transfers',
            link: '/counselling/track-changes',
          },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      { subMenuItems: [{ label: 'Summary', link: '/counselling/status' }] },
    ],
  },
];

const DSEADMIN: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Counselling', link: '/counselling/front-page' },
          { label: 'Seniority Update', link: '/counselling/seniority' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
          { label: 'Change Candidate', link: '/counselling/change-candidate' },
          {
            label: 'Change School Information',
            link: '/counselling/school-info',
          },
          {
            label: 'School Wise Transfers',
            link: '/counselling/track-changes',
          },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      { subMenuItems: [{ label: 'Summary', link: '/counselling/status' }] },
    ],
  },
];

const CEOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Counselling', link: '/counselling/front-page' },
          {
            label: 'Approval Teacher Transfer',
            link: '/counselling/teacher-transfer',
          },
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          {
            label: 'Seniority Challenge Approval',
            link: '/counselling/seniority-approval',
          },
          { label: 'Seniority Update', link: '/counselling/seniority' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'School Wise Transfers',
            link: '/counselling/track-changes',
          },
          {
            label: 'Inter District Transfers',
            link: '/counselling/inter-district',
          },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      { subMenuItems: [{ label: 'Summary', link: '/counselling/status' }] },
    ],
  },
  {
    label: 'Aided Schools',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Aided School Form', link: '/counselling/aided-sch-form' },
          {
            label: 'Aided School Approval',
            link: '/counselling/aided-sch-approval',
          },
          {
            label: 'Add / Update Vacancy',
            link: '/counselling/vacancy-update-aided-sch',
          },
          {
            label: 'Corporate Management',
            link: '/counselling/corporate-management',
          },
        ],
      },
    ],
  },
];

const BEOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  { label: 'Counselling', link: '/counselling/front-page' },
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
  { label: 'Summary', link: '/counselling/status' },
];

const DEOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Approval Teacher Transfer',
            link: '/counselling/teacher-transfer',
          },
          {
            label: 'Seniority Challenge Approval',
            link: '/counselling/seniority-approval',
          },
          { label: 'Seniority Update', link: '/counselling/seniority' },
          { label: 'Counselling', link: '/counselling/front-page' },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      { subMenuItems: [{ label: 'Summary', link: '/counselling/status' }] },
    ],
  },
  {
    label: 'Aided Schools',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Aided School Form', link: '/counselling/aided-sch-form' },
          {
            label: 'Add / Update Vacancy',
            link: '/counselling/vacancy-update-aided-sch',
          },
        ],
      },
    ],
  },
];

const AEOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  { label: 'Counselling', link: '/counselling/front-page' },
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
  {
    label: 'Seniority Challenge Approval',
    link: '/counselling/seniority-approval',
  },
];

const DCGCCMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Plan New Counselling',
            link: '/counselling/plan-new-counselling',
          },
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          { label: 'Candidate List', link: '/counselling/candidate' },
          { label: 'Vacancy', link: '/counselling/vacancy' },
          { label: 'Seniority Update', link: '/counselling/seniority' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          {
            label: 'Change School Information',
            link: '/counselling/school-info',
          },
          {
            label: 'Change Candidate Details',
            link: '/counselling/change-candidate',
          },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
          {
            label: 'Transfers in a school',
            link: '/counselling/track-changes',
          },
          { label: 'Control Panel', link: '/counselling/update-status' },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      { subMenuItems: [{ label: 'Summary', link: '/counselling/status' }] },
    ],
  },
];

const JUNIORASSISTANT: MenuItem[] = [
  {
    label: 'Aided Schools',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Aided School Form', link: '/counselling/aided-sch-form' },
          {
            label: 'Aided School Approval',
            link: '/counselling/aided-sch-approval',
          },
          {
            label: 'Add / Update Vacancy',
            link: '/counselling/vacancy-update-aided-sch',
          },
        ],
      },
    ],
  },
];

const PATOCEO: MenuItem[] = [
  {
    label: 'Aided Schools',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Aided School Approval',
            link: '/counselling/aided-sch-approval',
          },
          {
            label: 'Add / Update Vacancy',
            link: '/counselling/vacancy-update-aided-sch',
          },
        ],
      },
    ],
  },
];

const SUPERINTENDENT: MenuItem[] = [
  {
    label: 'Aided Schools',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Aided School Approval',
            link: '/counselling/aided-sch-approval',
          },
          {
            label: 'Add / Update Vacancy',
            link: '/counselling/vacancy-update-aided-sch',
          },
        ],
      },
    ],
  },
];

const MONITOR: MenuItem[] = [
  { label: 'Counselling Reset', link: '/counselling/counseling-reset' },
];

const DIETMENU: MenuItem[] = [
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
  { label: 'Counselling', link: '/counselling/front-page' },
];

const JDMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Plan New Counselling',
            link: '/counselling/plan-new-counselling',
          },
          {
            label: 'Approval Teacher Transfer',
            link: '/counselling/teacher-transfer',
          },
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          { label: 'Seniority Update', link: '/counselling/seniority' },
          { label: 'Counselling', link: '/counselling/front-page' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          {
            label: 'Change Candidate Details',
            link: '/counselling/change-candidate',
          },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Summary', link: '/counselling/status' },
          {
            label: 'Candidates Details',
            link: '/counselling/candidates-details',
          },
          {
            label: 'Teacher Transfer Report',
            link: '/counselling/TransferReport',
          },
        ],
      },
    ],
  },
];

const SCERTMENU: MenuItem[] = [
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
  { label: 'Counselling', link: '/counselling/front-page' },
];

const KALLARMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  {
    label: 'Before Counselling',
    subMenus: [
      {
        subMenuItems: [
          {
            label: 'Plan New Counselling',
            link: '/counselling/plan-new-counselling',
          },
          {
            label: 'Approval Teacher Transfer',
            link: '/counselling/teacher-transfer',
          },
          {
            label: 'Seniority Challenge Approval',
            link: '/counselling/seniority-approval',
          },
          {
            label: 'Application Summary',
            link: '/counselling/application-summary',
          },
          { label: 'Counselling', link: '/counselling/front-page' },
          { label: 'Seniority Update', link: '/counselling/seniority' },
        ],
      },
    ],
  },
  {
    label: 'During Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Live Updates', link: '/counselling/live-updates' },
          {
            label: 'Change Candidate Details',
            link: '/counselling/change-candidate',
          },
          { label: 'Change Vacancy', link: '/counselling/change-vacancy' },
        ],
      },
    ],
  },
  {
    label: 'After Counselling',
    subMenus: [
      {
        subMenuItems: [
          { label: 'Summary', link: '/counselling/status' },
          {
            label: 'Candidates Details',
            link: '/counselling/candidates-details',
          },
        ],
      },
    ],
  },
];

const AEEOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
];

const APOMENU: MenuItem[] = [
  { label: 'Dashboard', link: '/counselling/dashboard' },
  { label: 'Counselling', link: '/counselling/front-page' },
  { label: 'Approval Teacher Transfer', link: '/counselling/teacher-transfer' },
];

// ── Role resolver (matches Angular's if-else chain exactly) ──────────────────

function resolveMenu(userTypeId: string, userTypeId1: string): MenuItem[] {
  if (userTypeId === '9') return CEOMENU;
  if (userTypeId === '10') return DEOMENU;
  if (userTypeId === '6' && userTypeId1 !== '29') return BEOMENU;
  if (userTypeId === '5' && userTypeId1 === '1') return DEEADMIN;
  if (userTypeId === '5' && userTypeId1 === '2') return DSEADMIN;
  if (userTypeId === '5' && userTypeId1 === '29') return DCGCCMENU;
  if (userTypeId === '6' && userTypeId1 === '29') return AEOMENU;
  if (userTypeId === '5' && userTypeId1 === '4') return VIEWMENU;
  if (userTypeId === '63') return JUNIORASSISTANT;
  if (userTypeId === '41') return SUPERINTENDENT;
  if (userTypeId === '30' && (userTypeId1 === '2' || userTypeId1 === '3'))
    return PATOCEO;
  if (userTypeId === '5' && userTypeId1 === '5') return MONITOR;
  if (userTypeId === '22') return DIETMENU;
  if (userTypeId === '5' && userTypeId1 === '7') return SCERTMENU;
  if (userTypeId === '5' && userTypeId1 === '6') return JDMENU;
  if (userTypeId === '5' && userTypeId1 === '8') return KALLARMENU;
  if (userTypeId === '7' && userTypeId1 === '7') return AEEOMENU;
  if (userTypeId === '26' && userTypeId1 === '1') return APOMENU;
  return ADMINMENU;
}

// ── Helper ───────────────────────────────────────────────────────────────────

function hasItems(item: MenuItem): boolean {
  return Array.isArray(item.subMenus) && item.subMenus.length > 0;
}

// ── Component ────────────────────────────────────────────────────────────────

const MenuHeader = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { usertypeid, emisUsertype1 } = useSelector(
    (state: RootState) => state.session
  );

  const menuItems = resolveMenu(usertypeid, emisUsertype1);

  // Track which top-level item's submenu is open (hover or click on mobile)
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Clicking any menu item clears the order-copy hand-off row (and its
  // IsMutual marker) so a fresh navigation starts clean.
  const handleMenuClick = () => {
    dispatch(setRowData(''));
    setOpenIndex(null);
  };

  const handleMouseEnter = (index: number) => setOpenIndex(index);
  const handleMouseLeave = () => setOpenIndex(null);

  /** Is ANY sub-link in this item currently active? */
  const isItemActive = (item: MenuItem): boolean => {
    if (item.link && location.pathname === item.link) return true;
    if (item.subMenus) {
      return item.subMenus.some((sub) =>
        sub.subMenuItems.some((si) => location.pathname === si.link)
      );
    }
    return false;
  };

  return (
    <nav className="menu-header bottom-navbar">
      <div className="container">
        <ul className="nav page-navigation">
          {menuItems.map((item, index) => {
            const active = isItemActive(item);

            // Simple link — no submenu
            if (!hasItems(item)) {
              return (
                <li
                  key={item.label}
                  className={`nav-item${active ? ' active' : ''}`}
                >
                  <NavLink
                    className="nav-link"
                    to={item.link!}
                    onClick={handleMenuClick}
                  >
                    <span className="menu-title">{item.label}</span>
                  </NavLink>
                </li>
              );
            }

            // Item with submenu (standard or mega-menu)
            return (
              <li
                key={item.label}
                className={`nav-item${item.isMegaMenu ? ' mega-menu' : ''}${active ? ' active' : ''}`}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  className="nav-link"
                  aria-expanded={openIndex === index}
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                >
                  <span className="menu-title">{item.label}</span>
                  <i className="link-arrow" />
                </button>

                {openIndex === index && (
                  <div className="submenu">
                    {item.isMegaMenu ? (
                      <div className="row">
                        {item.subMenus!.map((subMenu, si) => (
                          <div className="col-md-3" key={si}>
                            <ul className="submenu-item">
                              {subMenu.subMenuItems
                                .filter((s) => !s.visible)
                                .map((sub) =>
                                  sub.isTitle ? (
                                    <li
                                      key={sub.label}
                                      className="category-heading"
                                    >
                                      {sub.label}
                                    </li>
                                  ) : (
                                    <li key={sub.label} className="nav-item">
                                      <NavLink
                                        className="nav-link"
                                        to={sub.link}
                                        onClick={handleMenuClick}
                                      >
                                        {sub.label}
                                      </NavLink>
                                    </li>
                                  )
                                )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      item.subMenus!.map((subMenu, si) => (
                        <ul className="submenu-item" key={si}>
                          {subMenu.subMenuItems
                            .filter((s) => !s.visible)
                            .map((sub) =>
                              sub.isTitle ? (
                                <li
                                  key={sub.label}
                                  className="category-heading"
                                >
                                  {sub.label}
                                </li>
                              ) : (
                                <li key={sub.label} className="nav-item">
                                  <NavLink
                                    className="nav-link"
                                    to={sub.link}
                                    onClick={handleMenuClick}
                                  >
                                    {sub.label}
                                  </NavLink>
                                  {sub.badge && (
                                    <span
                                      className={`badge bg-${sub.badge.variant}`}
                                    >
                                      {sub.badge.text}
                                    </span>
                                  )}
                                </li>
                              )
                            )}
                        </ul>
                      ))
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default MenuHeader;
